/* # AttachmentList Component
   - Displays a list of fault report attachments
   - Resolves URLs and categorizes file types
   - Removed download button as requested by user due to inconsistent browser behavior
*/
import { useState, useEffect } from 'react';
import {
  LuFileText,
  LuImage,
  LuEye,
  LuFileSpreadsheet,
  LuFile } from
'react-icons/lu';
import FileViewerModal from './FileViewerModal';

export default function AttachmentList({ attachments: rawAttachments }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (rawAttachments && rawAttachments.length > 0) {
      resolveAttachmentUrls();
    } else {
      setAttachments([]);
    }
  }, [rawAttachments]);

  const resolveAttachmentUrls = async () => {
    setLoading(true);
    try {
      const resolved = await Promise.all(
        rawAttachments.map(async (file) => {
          try {
            const url = file.file_url || file.fileUrl;
            if (url && (url.startsWith('http') || url.startsWith('blob:'))) {
              return { ...file, resolvedUrl: url };
            }

            const { data: resolvedUrl } = await window.ezsite.apis.getUploadUrl(url);
            return { ...file, resolvedUrl: resolvedUrl || url };
          } catch (e) {
            console.warn('[AttachmentList] Resolution failed:', e);
            return { ...file, resolvedUrl: file.file_url || file.fileUrl };
          }
        })
      );
      setAttachments(resolved);
    } catch (error) {
      console.error('[AttachmentList] Error resolving URLs:', error);
      setAttachments(rawAttachments);
    } finally {
      setLoading(false);
    }
  };

  const getFileExtension = (filename) => {
    if (!filename) return '';
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  const getFileInfo = (filename) => {
    const ext = getFileExtension(filename);

    if (['jpg', 'jpeg', 'png', 'webp', 'avif', 'gif', 'svg'].includes(ext)) {
      return { type: 'image', label: 'Image', color: 'text-blue-500', icon: LuImage, bg: 'bg-blue-50' };
    }
    if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return { type: 'spreadsheet', label: 'Spreadsheet', color: 'text-green-600', icon: LuFileSpreadsheet, bg: 'bg-green-50' };
    }
    if (ext === 'pdf') {
      return { type: 'pdf', label: 'PDF Document', color: 'text-red-500', icon: LuFileText, bg: 'bg-red-50' };
    }
    if (['doc', 'docx'].includes(ext)) {
      return { type: 'document', label: 'Word Document', color: 'text-indigo-500', icon: LuFileText, bg: 'bg-indigo-50' };
    }
    return { type: 'file', label: 'File', color: 'text-gray-500', icon: LuFile, bg: 'bg-gray-50' };
  };

  const getFileName = (file) => {
    if (file.file_name) return file.file_name;
    if (file.document_name) return file.document_name;
    try {
      const url = file.file_url || file.fileUrl;
      const parts = url.split('/');
      const lastPart = parts[parts.length - 1];
      return decodeURIComponent(lastPart).replace(/^[a-z0-9-]{36}_/i, '');
    } catch {
      return 'Attachment';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 py-4">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Resolving attachments...</p>
      </div>);

  }

  if (!attachments || attachments.length === 0) return null;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {attachments.map((file) => {
          const fileName = getFileName(file);
          const info = getFileInfo(fileName);
          const Icon = info.icon;
          const url = file.resolvedUrl;

          return (
            <div
              key={file.id || file.ID}
              className="group relative flex items-center gap-4 p-4 bg-white rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all">

              <div
                className={`flex-shrink-0 w-12 h-12 ${info.bg} rounded-xl flex items-center justify-center shadow-sm overflow-hidden cursor-pointer`}
                onClick={() => setSelectedFile({ url, fileName, type: info.type })}>

                {info.type === 'image' ?
                <img src={url} alt="Thumbnail" className="w-full h-full object-cover" /> :

                <Icon className={`w-6 h-6 ${info.color}`} />
                }
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate group-hover:text-blue-700 transition-colors" title={fileName}>
                  {fileName}
                </p>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                  {info.label} • {getFileExtension(fileName).toUpperCase()}
                </p>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setSelectedFile({ url, fileName, type: info.type })}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                  title="View File">

                  <LuEye className="w-5 h-5" />
                </button>
              </div>
            </div>);

        })}
      </div>

      {selectedFile &&
      <FileViewerModal
        {...selectedFile}
        onClose={() => setSelectedFile(null)} />

      }
    </>);

}